import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AdminCrudShell } from '@/components/admin/AdminCrudShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Search } from 'lucide-react';
import {
  fetchAdminPaymentInspect, fetchAdminRedsysConfig,
  fetchAdminList, formatMoney, friendlyError,
} from '@/lib/api';
import { toast } from 'sonner';

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'refunded', label: 'Reembolsado' },
];

const COLOR = {
  confirmed: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]',
  pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-[var(--ns-red)]/15 text-[var(--ns-red)]',
  refunded: 'bg-blue-100 text-blue-800',
};

// --- Formatters ---------------------------------------------------------

const formatOrderRef = (dsOrder) => {
  if (!dsOrder) return '—';
  const s = String(dsOrder).toLowerCase().replace(/[^a-z0-9]/g, '');
  // 12 chars max para lectura cómoda. Nunca modifica el valor persistido.
  return `#${s.slice(0, 12)}`;
};

const formatDateHuman = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  // Timezone del navegador. Formato "dd/mm/yyyy · hh:mm".
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} · ${hh}:${mi}`;
};

const fullName = (u) => {
  if (!u) return '—';
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.email || '—';
};

// --- Config strip -------------------------------------------------------

const RedsysConfigStrip = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchAdminRedsysConfig();
        setCfg(data);
      } catch (err) {
        if (err?.response?.status !== 403) toast.error(friendlyError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  if (loading) return <Skeleton className="h-16 w-full rounded-xl mb-4" data-testid="admin-redsys-config-loading" />;
  if (!cfg) return null;
  const Item = ({ label, value }) => (
    <div className="flex flex-col text-xs">
      <span className="text-[var(--ns-text-subtle)] uppercase tracking-wide">{label}</span>
      <span className="font-mono text-[var(--ns-text)]">{value}</span>
    </div>
  );
  return (
    <div
      data-testid="admin-redsys-config"
      className="mb-4 rounded-xl border border-[var(--ns-border)] bg-white px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3"
    >
      <Item label="Redsys" value={cfg.redsys_enabled ? 'HABILITADO' : 'DESHABILITADO'} />
      <Item label="Entorno" value={(cfg.redsys_env || '—').toUpperCase()} />
      <Item label="Merchant" value={cfg.merchant_code || '—'} />
      <Item label="Terminal" value={cfg.terminal || '—'} />
      <Item label="Notif URL" value={cfg.notification_url ? '✅ configurada' : '—'} />
      <Item label="URL OK" value={cfg.url_ok ? '✅ configurada' : '—'} />
      <Item label="URL KO" value={cfg.url_ko ? '✅ configurada' : '—'} />
      <Item label="Secret Key" value={cfg.secret_key_configured ? '✅ configurada' : '❌ ausente'} />
    </div>
  );
};

// --- Inspect dialog -----------------------------------------------------

const KV = ({ label, value, mono = true }) => (
  <div className="flex flex-col py-1.5 border-b border-[var(--ns-border)]/50 last:border-0">
    <span className="text-[10px] uppercase tracking-wide text-[var(--ns-text-subtle)]">{label}</span>
    <span className={`text-sm ${mono ? 'font-mono' : ''} text-[var(--ns-text)] break-all`}>
      {value === null || value === undefined || value === '' ? '—' : String(value)}
    </span>
  </div>
);

const Section = ({ title, children, testId }) => (
  <div className="rounded-xl border border-[var(--ns-border)] bg-white p-4" data-testid={testId}>
    <h3 className="text-sm font-semibold text-[var(--ns-gold-700)] mb-2">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">{children}</div>
  </div>
);

const InspectDialog = ({ paymentId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!paymentId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchAdminPaymentInspect(paymentId);
        setData(res);
      } catch (err) {
        toast.error(friendlyError(err));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [paymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = !!paymentId;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[var(--ns-bg)]" data-testid="admin-payment-inspect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Detalle de transacción</DialogTitle>
          <DialogDescription>Vista sanitizada. Solo lectura. No expone secretos ni datos completos de tarjeta.</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          {loading || !data ? (
            <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
          ) : (
            <div className="space-y-4">
              {data.incidence ? (
                <div
                  data-testid="admin-payment-inspect-incidence"
                  className="flex items-start gap-2 rounded-xl border border-[var(--ns-red)]/40 bg-[var(--ns-red)]/5 px-3 py-2"
                >
                  <AlertTriangle className="h-4 w-4 text-[var(--ns-red)] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--ns-red)] font-semibold">Incidencia detectada</div>
                    <div className="text-sm font-mono">{data.incidence}</div>
                  </div>
                </div>
              ) : null}

              <Section title="Payment" testId="admin-inspect-payment">
                <KV label="Payment ID" value={data.payment?.id} />
                <KV label="Tipo operación" value={data.payment?.operation_type} />
                <KV label="Provider" value={data.payment?.provider} />
                <KV label="Status" value={data.payment?.status} />
                <KV label="Amount" value={formatMoney(data.payment?.amount_cents, data.payment?.currency)} mono={false} />
                <KV label="Currency" value={data.payment?.currency} />
                <KV label="Provider Payment ID (Ds_Order)" value={data.payment?.provider_payment_id} />
                <KV label="Result category" value={data.payment?.provider_result_category} />
                <KV label="Attempt #" value={data.payment?.attempt_number} />
                <KV label="Idempotency key" value={data.payment?.idempotency_key} />
                <KV label="Created" value={data.payment?.created_at} />
                <KV label="Updated" value={data.payment?.updated_at} />
              </Section>

              <Section title="Usuario" testId="admin-inspect-user">
                {data.user ? (
                  <>
                    <KV label="User ID" value={data.user.id} />
                    <KV label="Email" value={data.user.email} mono={false} />
                    <KV label="Nombre" value={`${data.user.first_name || ''} ${data.user.last_name || ''}`.trim()} mono={false} />
                  </>
                ) : <KV label="Usuario" value="No disponible" />}
              </Section>

              {data.checkout_session ? (
                <Section title="Checkout session" testId="admin-inspect-checkout">
                  <KV label="Session ID" value={data.checkout_session.id} />
                  <KV label="Status" value={data.checkout_session.status} />
                  <KV label="Provider" value={data.checkout_session.provider} />
                  <KV label="Amount" value={formatMoney(data.checkout_session.amount_cents, data.checkout_session.currency)} mono={false} />
                  <KV label="Currency" value={data.checkout_session.currency} />
                  <KV label="Provider session ID" value={data.checkout_session.provider_session_id} />
                  <KV label="Recurring" value={data.checkout_session?.metadata?.recurring ? 'SÍ' : 'NO'} />
                  <KV label="Plan (metadata)" value={data.checkout_session?.metadata?.plan_id} />
                </Section>
              ) : null}

              {data.subscription ? (
                <Section title="Subscription" testId="admin-inspect-subscription">
                  <KV label="Subscription ID" value={data.subscription.id} />
                  <KV label="Plan" value={data.plan?.name || data.subscription.plan_id} mono={false} />
                  <KV label="Status" value={data.subscription.status} />
                  <KV label="Billing period" value={data.plan?.billing_period} />
                  <KV label="Billing period snapshot" value={data.subscription.billing_period_snapshot} />
                  <KV label="Starts at" value={data.subscription.starts_at} />
                  <KV label="Current period start" value={data.subscription.current_period_start} />
                  <KV label="Current period end" value={data.subscription.current_period_end} />
                  <KV label="Next renewal at" value={data.subscription.next_renewal_at} />
                  <KV label="Cancel at period end" value={data.subscription.cancel_at_period_end ? 'SÍ' : 'NO'} />
                  <KV label="Cancelled at" value={data.subscription.cancelled_at} />
                  <KV label="Payment method ID" value={data.subscription.payment_method_id} />
                  <KV label="Notes / activation blocked" value={data.subscription.notes} mono={false} />
                </Section>
              ) : null}

              {data.payment_method ? (
                <Section title="Payment method" testId="admin-inspect-pm">
                  <KV label="PM ID" value={data.payment_method.id} />
                  <KV label="Provider" value={data.payment_method.provider} />
                  <KV label="Status" value={data.payment_method.status} />
                  <KV label="Brand" value={data.payment_method.brand} />
                  <KV label="Last 4" value={data.payment_method.last4} />
                  <KV label="Expiry" value={
                    data.payment_method.expiry_month && data.payment_method.expiry_year
                      ? `${String(data.payment_method.expiry_month).padStart(2, '0')}/${data.payment_method.expiry_year}`
                      : null
                  } />
                  <KV label="COF Identifier disponible" value={data.payment_method.cof_identifier_available ? 'SÍ' : 'NO'} />
                  <KV label="COF TxnId disponible" value={data.payment_method.cof_txnid_available ? 'SÍ' : 'NO'} />
                  <KV label="Last used at" value={data.payment_method.last_used_at} />
                </Section>
              ) : null}

              {data.entitlement ? (
                <Section title="Entitlement" testId="admin-inspect-entitlement">
                  <KV label="Entitlement ID" value={data.entitlement.id} />
                  <KV label="Status" value={data.entitlement.status} />
                  <KV label="Starts at" value={data.entitlement.starts_at} />
                  <KV label="Ends at" value={data.entitlement.ends_at} />
                  <KV label="Acceso activo" value={data.entitlement.access_active ? 'SÍ' : 'NO'} />
                </Section>
              ) : null}

              {data.cycle_lock ? (
                <Section title="Ciclo / renovación" testId="admin-inspect-cycle">
                  <KV label="Billing period key" value={data.cycle_lock.billing_period_key} />
                  <KV label="State" value={data.cycle_lock.state} />
                  <KV label="Financial attempt #" value={data.cycle_lock.financial_attempt_number} />
                  <KV label="Attempt #" value={data.cycle_lock.attempt_number} />
                  <KV label="Retry at" value={data.cycle_lock.retry_at} />
                  <KV label="First failure at" value={data.cycle_lock.first_failure_at} />
                  <KV label="Last failure at" value={data.cycle_lock.last_failure_at} />
                  <KV label="Possible duplicate charge" value={data.cycle_lock.possible_duplicate_charge ? 'SÍ' : 'NO'} />
                </Section>
              ) : null}

              <Section title="Redsys (sanitizado)" testId="admin-inspect-redsys">
                <KV label="Ds_Order" value={data.redsys?.Ds_Order} />
                <KV label="Ds_Response" value={data.redsys?.Ds_Response} />
                <KV label="Authorization code" value={data.redsys?.Ds_AuthorisationCode} />
                <KV label="Result/category" value={data.redsys?.provider_result_category} />
                <KV label="Ds_Amount" value={data.redsys?.Ds_Amount} />
                <KV label="Ds_Currency" value={data.redsys?.Ds_Currency} />
                <KV label="Ds_TransactionType" value={data.redsys?.Ds_TransactionType} />
                <KV label="Ds_ProcessedPayMethod" value={data.redsys?.Ds_ProcessedPayMethod} />
                <KV label="Card country" value={data.redsys?.Ds_Card_Country} />
                <KV label="Card brand" value={data.redsys?.Ds_Card_Brand} />
                <KV label="Card type" value={data.redsys?.Ds_Card_Type} />
                <KV label="Fecha (Ds_Date)" value={data.redsys?.Ds_Date} />
                <KV label="Hora (Ds_Hour)" value={data.redsys?.Ds_Hour} />
                <KV label="Correlation ID" value={data.redsys?.correlation_id} />
                <KV label="Identifier COF recibido" value={data.redsys?.identifier_received ? 'SÍ' : 'NO'} />
                <KV label="Cof_Txnid recibido" value={data.redsys?.cof_txnid_received ? 'SÍ' : 'NO'} />
              </Section>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// --- User map hook (batch fetch admin users for name resolution) --------

function useAdminUserMap() {
  const [map, setMap] = useState({});
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        // Recorremos páginas de 100 (máximo permitido por el endpoint)
        // para construir el mapa de user_id → user. Suficiente para el
        // volumen actual; el modal Detalle además trae `user` completo.
        const m = {};
        let page = 1;
        // Límite de seguridad: máximo 10 páginas (1000 users). Evita loops.
        while (page <= 10) {
          // eslint-disable-next-line no-await-in-loop
          const res = await fetchAdminList('users', { page, page_size: 100 });
          const items = res?.items || [];
          for (const u of items) m[u.id] = u;
          const totalPages = res?.total_pages ?? 1;
          if (page >= totalPages || items.length === 0) break;
          page += 1;
        }
        setMap(m);
      } catch {
        // Silencioso — la tabla cae a mostrar user_id abreviado.
      }
    })();
  }, []);
  return map;
}

// --- Row action ---------------------------------------------------------

const InspectAction = ({ row, onInspect }) => (
  <Button
    size="sm" variant="ghost"
    onClick={() => onInspect(row.id)}
    className="h-8 px-3 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
    data-testid={`admin-payments-inspect-${row.id}`}
    title="Ver detalle"
  >
    <Search className="h-3.5 w-3.5 mr-1.5" /> Detalle
  </Button>
);

// --- Page ---------------------------------------------------------------

export default function AdminPayments() {
  const [inspectId, setInspectId] = useState(null);
  const userMap = useAdminUserMap();

  const columns = useMemo(() => ([
    {
      key: 'provider_payment_id',
      label: 'Pedido',
      className: 'min-w-[170px]',
      render: (r) => (
        <span
          className="text-sm font-mono text-[var(--ns-text)]"
          data-testid="payment-order-ref"
          title={r.provider_payment_id || ''}
        >
          {formatOrderRef(r.provider_payment_id)}
        </span>
      ),
    },
    {
      key: 'user_id',
      label: 'Usuario',
      className: 'min-w-[260px]',
      render: (r) => {
        // Preferimos los campos enriquecidos del endpoint (user_name/user_email);
        // mantenemos el fallback por userMap para no romper vistas previas.
        const u = userMap[r.user_id];
        const name = (r.user_name && r.user_name.trim()) || (u ? fullName(u) : '');
        const email = r.user_email || (u ? u.email : '') || '';
        if (!name && !email) {
          return (
            <span className="text-sm text-[var(--ns-text-muted)]" data-testid="payment-user-name">
              Correo no disponible
            </span>
          );
        }
        if (!name) {
          return (
            <span className="text-xs text-[var(--ns-text)]" data-testid="payment-user-email">
              {email}
            </span>
          );
        }
        return (
          <div className="flex flex-col leading-tight" data-testid="payment-user-cell">
            <span className="text-sm font-medium text-[var(--ns-text)]" title={name} data-testid="payment-user-name">{name}</span>
            <span className="text-xs text-[var(--ns-text-muted)]" title={email} data-testid="payment-user-email">
              {email || 'Correo no disponible'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Estado',
      className: 'min-w-[120px]',
      render: (r) => (
        <Badge className={`text-[10px] uppercase ${COLOR[r.status] || ''}`}>{r.status}</Badge>
      ),
    },
    {
      key: 'amount_cents',
      label: 'Importe',
      className: 'min-w-[110px] text-right',
      render: (r) => (
        <span className="tabular-nums text-sm">{formatMoney(r.amount_cents, r.currency)}</span>
      ),
    },
    {
      key: 'created_at',
      label: 'Fecha',
      className: 'min-w-[170px]',
      render: (r) => (
        <span className="text-sm font-mono text-[var(--ns-text-muted)]" data-testid="payment-created-at">
          {formatDateHuman(r.created_at)}
        </span>
      ),
    },
  ]), [userMap]);

  const RowActions = React.useCallback(
    ({ row }) => <InspectAction row={row} onInspect={setInspectId} />,
    [],
  );

  return (
    <>
      {/* Ocultar botón "Editar" heredado de AdminCrudShell para pagos (READ-ONLY),
          sin modificar el shell compartido. Solo scoped a esta pantalla. */}
      <style>{`
        .admin-payments-scope [data-testid^="admin-payments-edit-"] { display: none !important; }
      `}</style>
      <div className="admin-payments-scope">
        <div className="max-w-6xl mx-auto px-4 pt-2 -mb-2">
          <RedsysConfigStrip />
        </div>
        <AdminCrudShell
          title="Pagos"
          subtitle="Registro de cada intento de pago procesado por la plataforma. Solo lectura."
          entity="payments"
          testIdPrefix="admin-payments"
          readOnly
          canDelete={false}
          canEdit={false}
          searchPlaceholder="Buscar usuario o email…"
          rowActions={RowActions}
          formFields={[]}
          filters={[{ name: 'status', label: 'Estado', options: STATUS_OPTS }]}
          columns={columns}
        />
        <InspectDialog paymentId={inspectId} onClose={() => setInspectId(null)} />
      </div>
    </>
  );
}
